// Backend API utilities for course access

// Backend API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';


// Check if user has access to a specific course
export const hasCourseAccess = async (email: string, courseId: string): Promise<boolean> => {
  try {
    // Add cache-busting parameter
    const timestamp = Date.now();
    const response = await fetch(`${API_BASE_URL}/api/check-access?t=${timestamp}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, courseId }),
    });
    
    if (!response.ok) {
      console.error('API request failed:', response.status);
      return false;
    }
    
    const data = await response.json();
    return data.hasAccess;
  } catch (error) {
    console.error('Error checking course access:', error);
    return false;
  }
};

// Get all courses user has access to
export const getUserCourses = async (email: string): Promise<string[]> => {
  // Retry logic for failed requests
  const maxRetries = 3;
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // First, check if backend is healthy
      try {
        const healthResponse = await fetch(`${API_BASE_URL}/api/health`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (!healthResponse.ok) {
          throw new Error(`Backend health check failed: ${healthResponse.status}`);
        }
      } catch (healthError) {
        if (attempt === maxRetries) {
          return [];
        }
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        continue;
      }
      
      // Add cache-busting parameter
      const timestamp = Date.now();
      const url = `${API_BASE_URL}/api/user-courses?t=${timestamp}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email }),
      });
      
      if (!response.ok) {
        if (attempt === maxRetries) {
          return [];
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        continue;
      }
      
      const data = await response.json();
      return data.courses;
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
  
  return [];
};

